// ============================================================================
// MarketDisputeDecisionForm — решение оператора по спору.
// Три варианта: возврат стоимости взятия (деньги — существующая серверная
// операция), выплата мастеру и отказ (фиксация решения без движения средств).
// ============================================================================

import { useState } from 'react';
import { CheckCircle2, Gavel, Loader2, Undo2, Wallet, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToastStore } from '@/store/toastStore';
import { confirmDialog } from '@/lib/dialogs';
import {
  DISPUTE_DECISIONS,
  type DisputeDecision,
} from '@/lib/marketDispute';

const DECISION_ICONS: Record<DisputeDecision, typeof Undo2> = {
  refund: Undo2,
  payout: Wallet,
  reject: XCircle,
};

export function MarketDisputeDecisionForm({
  requestId,
  canRefund,
  refundableHint,
  busy,
  disabledReason = '',
  onResolve,
}: {
  requestId: string;
  canRefund: boolean;
  refundableHint: string;
  busy: boolean;
  /** Решение недоступно (например, нет доступа к обсуждению заявки). */
  disabledReason?: string;
  onResolve: (decision: DisputeDecision, comment: string) => void;
}) {
  const showError = useToastStore((s) => s.showError);
  const [decision, setDecision] = useState<DisputeDecision | null>(null);
  const [comment, setComment] = useState('');

  const selected = DISPUTE_DECISIONS.find((d) => d.value === decision);

  const submit = async () => {
    if (!decision) return;
    if (!comment.trim()) {
      showError('Укажите обоснование решения');
      return;
    }
    if (decision === 'refund') {
      const ok = await confirmDialog(
        'Оформить возврат стоимости взятия мастеру? Сумма определяется сервером по фактическому списанию; повторный возврат не удваивается.',
      );
      if (!ok) return;
    }
    onResolve(decision, comment);
  };

  return (
    <>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Gavel className="h-4 w-4 text-primary" />
        Решение оператора
      </div>
      <div className="mt-1 text-[11px] leading-5 text-muted-foreground">
        Денежное решение выполняет сервер. «Возврат» вернёт мастеру стоимость взятия;
        «выплата» и «отказ» фиксируются без движения средств.
      </div>
      {disabledReason && (
        <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-800">
          {disabledReason}
        </div>
      )}
      <div className="mt-3 grid gap-2">
        {DISPUTE_DECISIONS.map((option) => {
          const Icon = DECISION_ICONS[option.value];
          const active = decision === option.value;
          const disabled = option.value === 'refund' && !canRefund;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !disabled && setDecision(option.value)}
              disabled={busy || disabled || !!disabledReason}
              className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${
                active ? 'border-primary bg-primary/10' : 'hover:bg-accent'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium">{option.label}</span>
                <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">{option.hint}</span>
              </span>
              {active && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
      </div>
      {refundableHint && (
        <div className="mt-2 text-[11px] leading-4 text-muted-foreground">{refundableHint}</div>
      )}
      {decision && (
        <div className="mt-3 space-y-2">
          <label className="block text-[11px] font-medium text-muted-foreground" htmlFor={`dispute-decision-${requestId}`}>
            Обоснование <span className="text-destructive">*</span>
          </label>
          <textarea
            id={`dispute-decision-${requestId}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={selected?.hint || 'Что проверили и как приняли решение'}
            className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            disabled={busy}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 flex-1 rounded-xl"
              onClick={() => { setDecision(null); setComment(''); }}
              disabled={busy}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="h-10 flex-1 rounded-xl"
              onClick={() => void submit()}
              disabled={busy || !comment.trim() || !!disabledReason}
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Gavel className="mr-1.5 h-4 w-4" />}
              Подтвердить
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
